import React, { Component } from 'react'
import * as PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { IntlProvider, addLocaleData } from 'react-intl'
import { get, isEmpty } from 'lodash'
import { asyncConnect } from 'redux-connect'
import { renderRoutes } from 'react-router-config'
import ReactGA from 'react-ga4'
import moment from 'moment'
import queryString from 'query-string'
import prototypes from 'utils/prototypes'
import { isLoaded as isAuthLoaded, load as loadAuth, setActiveOrg } from 'redux/modules/auth'
import { changeLocale, toggleOnlineStatus, getLocalizedMessages } from 'redux/modules/app'
import { Utils, ActivityMonitor, AlreadyLoggedInModal } from 'containers/misc'
import { getDictonary, onIntlError } from 'utils/intl'
import { messages, intlData } from 'constants/locales'
import { apiLocales, apiOrganizations } from 'helpers/decorators/api'
import { refreshToken } from 'helpers/decorators'
import ga from 'utils/ga'
import { Notification, OfflineBanner, CookieConsent, RefreshLayer, MobileAppLink } from 'components/misc'
import 'assets/css/main.styl'
import InitPage from './misc/InitPage/InitPage'
import { matchPath } from 'react-router'
import config from '../config'
import {
  getCustomHomePageCode,
  getManualLangSwitch,
  setCustomHomePageCode,
  setManualLangSwitch
} from '../utils/cookies'
import AccessVerificationProvider from 'components/providers/AccessVerificationProvider'

@asyncConnect([
  {
    promise: ({ store: { dispatch, getState } }) => {
      const promises = []

      const { locale, messages } = getState().app

      const areMessagesLoaded = !isEmpty(messages[locale.code])

      if (!areMessagesLoaded) {
        promises.push(dispatch(getLocalizedMessages(locale.code)))
      }

      if (!isAuthLoaded(getState())) {
        promises.push(dispatch(loadAuth()))
      }

      return Promise.all(promises)
    }
  }
])
@apiLocales({ loadLocales: true, loadIfEmpty: true })
@apiOrganizations({ loadCustomization: true })
@refreshToken()
@connect(
  ({ app, api, auth }) => {
    const locale = app.locale
    const translatedMessages = app.messages[locale.code]
    const customization = api.organizations.customization
    return {
      refreshPage: app.misc.refreshPage,
      manualLanguageSwitch: app.misc.manualLanguageSwitch,
      locale,
      activeOrganizationId: get(auth, 'organization.id'),
      user: auth.user,
      isOffline: app.isOnline === false,
      translatedMessages: !isEmpty(translatedMessages) ? translatedMessages : messages[locale.code],
      customization: customization
    }
  },
  { changeLocale, setActiveOrg, toggleOnlineStatus }
)

class App extends Component {
  static propTypes = {
    changeLocale: PropTypes.func.isRequired,
    translatedMessages: PropTypes.object,
    locale: PropTypes.object.isRequired,
    toggleOnlineStatus: PropTypes.func,
    isOffline: PropTypes.bool,
    location: PropTypes.object,
    locales: PropTypes.object,
    refreshPage: PropTypes.bool,
    manualLanguageSwitch: PropTypes.bool,
    onGetOrganizationClientCustomization: PropTypes.func,
    customization: PropTypes.object
  }

  constructor (props) {
    super(props)

    this.state = {
      shouldRefresh: false
    }
  }

  componentDidMount = async () => {
    prototypes()
    const { locale } = this.props

    this.migrateLocaleToCookies()

    if (get(this.props, 'user.access_token')) {
      ReactGA.event({ category: 'Participant', event: 'App loaded - logged in from past' })
    } else {
      ReactGA.event({ category: 'Unauthenticated', event: 'App loaded' })
    }

    try {
      this.handleEnableAnalytics()
      window.addEventListener('online', () => this.props.toggleOnlineStatus(true))
      window.addEventListener('offline', () => this.props.toggleOnlineStatus(false))
    } catch (err) {
      console.error('Error setting up analytics')
    }

    const match = matchPath(get(this.props, 'location.pathname'), {
      path: `${config.publicPath}/:code([a-z0-9]{2})`,
      exact: true,
      strict: false
    })


    if (match) {
      const code = get(match, 'params.code', "")

      const storedCode = getCustomHomePageCode()

      if(storedCode !== code){
        setManualLangSwitch(false)
        setCustomHomePageCode(code)
        await this.props.onGetOrganizationClientCustomization(code)

        const customLocale = get(this.props, 'organizations.customization.locale')

        this.handleLocale(get(customLocale, 'code'))
      } else {

          if(getManualLangSwitch()){
               this.handleLocale(locale.code)
          } else {
            await this.props.onGetOrganizationClientCustomization(code)
            const customLocale = get(this.props, 'organizations.customization.locale')
            this.handleLocale(get(customLocale, 'code'))
          }
      }

    } else {
      this.handleLocale(locale.code)
    }
  }

  componentWillReceiveProps = async (nextProps) => {
    const { locale } = this.props

    if (get(this.props, 'organizations.customization.locale') !== get(nextProps, 'organizations.customization.locale') ||
      (get(nextProps, 'organizations.customization.locale.code') && get(this.props, 'locale.code') !== get(nextProps, 'organizations.customization.locale.code'))) {

      if(!getManualLangSwitch()){
        const newLocale = get(nextProps, 'organizations.customization.locale')
        changeLocale(newLocale)
        this.handleLocale(get(newLocale, 'code'))
        if (get(newLocale, 'id') !== locale.id) {
          setManualLangSwitch(false)
          window.location.reload()
        }
      }
    }

      // refresh change locale manual in lang switcher, no custom
      if (nextProps.refreshPage && nextProps.refreshPage !== this.props.refreshPage) {
        this.setState({ shouldRefresh: true }, () => {
          setManualLangSwitch(true)
          window.location.reload()
        })
      }

    const nextQuery = queryString.parse(get(nextProps, 'location.search'))
    const nextLocale = get(nextQuery, 'locale')

    if (
      nextLocale &&
      nextProps.locales.loaded !== this.props.locales.loaded
    ) {
      this.handleGetURLLocale(nextProps.locales)
    }

    const nextLocaleCode = get(nextProps, 'locale.code')

    if (locale.code !== nextLocaleCode) {
      this.handleLocale(get(nextProps, 'locale.code'))
    }

    // log page view
    if (get(this.props, 'location.pathname') !== get(nextProps, 'location.pathname')) {
      ReactGA.send({ hitType: "pageview", page: get(nextProps, 'location.pathname') })
    }

  }

  // User might have saved locale in localStorage
  // move it to cookie and delete it
  migrateLocaleToCookies = () => {
    if ('localStorage' in window) {
      const locale = localStorage.getItem('locale')

      if (locale) {
        try {
          localStorage.removeItem('locale')
          this.props.changeLocale(JSON.parse(locale))
          window.location.reload()
        } catch (err) {
          console.log(err)
        }
      }
    }
  }


  handleGetURLLocale = locales => {
    const query = queryString.parse(get(this.props, 'location.search'))
    const queryLocale = get(query, 'locale')
    const urlLocale = locales.data.find(loc => loc.code === queryLocale)

    urlLocale && this.props.changeLocale(urlLocale)
  }

  handleLocale = locale => {
    addLocaleData(getDictonary(locale))
    moment.locale(locale)
  }

  handleEnableAnalytics = () => {
    ga({ hitType: "pageview", page: get(this.props, 'location.pathname') })
  }

  render () {
    const { route, isOffline, locale, translatedMessages } = this.props

    return (
      <div>
        <IntlProvider
          locale={locale.code}
          formats={intlData[locale.code]}
          messages={translatedMessages}
          onError={onIntlError}
        >
          <div className='page__wrapper'>
            {isOffline && <OfflineBanner />}
            <AccessVerificationProvider>
              {renderRoutes(route.routes, { currentLocale: locale.code })}
            </AccessVerificationProvider>
            <Notification />
            <Utils />
            {this.state.shouldRefresh && <RefreshLayer />}
            <CookieConsent />
            <ActivityMonitor />
            <MobileAppLink />
            <AlreadyLoggedInModal />
          </div>
        </IntlProvider>
        <InitPage />
      </div>
    )
  }
}

export default App
